import { useToast } from "@chakra-ui/react";
import React, { useState } from "react";
import { Mutation, MutationFn, Query } from "react-apollo";
import { RouteComponentProps } from "react-router";
import { ObservatoryBundleFields } from "./components/Form";
import { ObservatoryBundleForm } from "./components/ObservatoryBundleForm";
import {
  adminGetObservatoryBundleQuery,
  updateObservatoryBundleMutation,
  UpdateObservatoryBundleResult,
  UpdateObservatoryBundleVariables,
  AdminGetObservatoryBundleResult,
  AdminGetObservatoryBundleVariables,
} from "./queries";

const TOAST_DURATION = 5000;
const SUBMIT_BUTTON_ENABLED_DELAY = 500;

type UpdateObservatoryBundleMutationFn = MutationFn<
  UpdateObservatoryBundleResult,
  UpdateObservatoryBundleVariables
>;

export const EditObservatoryBundle = (
  props: RouteComponentProps<{ id: string }>
) => {
  const id = props.match.params.id || "";
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const toast = useToast({ duration: TOAST_DURATION, isClosable: true });

  const delayedFormSubmissionComplete = () => {
    setTimeout(() => {
      setIsFormSubmitting(false);
    }, SUBMIT_BUTTON_ENABLED_DELAY);
  };

  const handleFormSubmit =
    (mutation: UpdateObservatoryBundleMutationFn) =>
    async (data: ObservatoryBundleFields) => {
      try {
        setIsFormSubmitting(true);
        const result = await mutation({
          variables: {
            id,
            observatoryBundle: {
              name: data.name,
              description: data.description,
              isActive: data.isActive,
              roles: data.observatoryBundlesRole ? ["ObservatoryBundles"] : [],
              marketArrayReportId: data.marketArrayReport?.value,
              observatoryReportId: data.observatoryReport?.value,
              insightsReportId: data.insightsReport?.value,
              drilldownId: data.drilldown?.value,
            },
          },
        });

        const message = result?.data?.updateObservatoryBundle?.message;
        const success = result?.data?.updateObservatoryBundle?.success;

        if (!success) {
          toast({
            title: "Error",
            description: message,
            status: "error",
          });
          return;
        }

        toast({
          title: "Success",
          description: message,
          status: "success",
        });
      } catch (err) {
        console.log("Error caught in EditObservatoryBundle", err);
        toast({
          title: "Error",
          description: "An unknown error occurred.",
          status: "error",
        });
      } finally {
        delayedFormSubmissionComplete();
      }
    };

  return (
    <Query<AdminGetObservatoryBundleResult, AdminGetObservatoryBundleVariables>
      query={adminGetObservatoryBundleQuery}
      variables={{ id }}
    >
      {({ data, loading, error }) => {
        if (loading) return <p>Loading...</p>;
        if (error) return <p>Error fetching Observatory Bundle</p>;
        return (
          <Mutation<
            UpdateObservatoryBundleResult,
            UpdateObservatoryBundleVariables
          >
            mutation={updateObservatoryBundleMutation}
          >
            {(mutation) => {
              return (
                <ObservatoryBundleForm
                  isFormSubmitting={isFormSubmitting}
                  observatoryBundle={data?.adminGetObservatoryBundle}
                  onSubmit={handleFormSubmit(mutation)}
                  title="Edit Observatory Bundle"
                  buttonText="Update"
                  buttonLoadingText="Updating..."
                />
              );
            }}
          </Mutation>
        );
      }}
    </Query>
  );
};
